import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Shield, CheckCircle, Clock, AlertCircle, Upload, User, FileText, Camera, X, Image } from 'lucide-react';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const KYC = () => {
  const [kycStatus, setKycStatus] = useState('not_started'); // not_started, pending, approved, rejected
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    // Personal Info
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    nationality: '',
    // Address
    address: '',
    city: '',
    country: 'UAE',
    postalCode: '',
    // Documents
    idType: 'passport',
    idNumber: '',
    idFront: null,
    idBack: null,
    selfie: null
  });

  // File input refs
  const idFrontRef = useRef(null);
  const idBackRef = useRef(null);
  const selfieRef = useRef(null);

  useEffect(() => {
    // Check existing KYC status from localStorage (in real app, fetch from API)
    const savedKyc = localStorage.getItem('kyc_status');
    if (savedKyc) {
      setKycStatus(savedKyc);
    }
  }, []);

  const handleFileSelect = (field, event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file (JPG, PNG, etc.)');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    // Convert to base64 for preview and storage
    const reader = new FileReader();
    reader.onload = (e) => {
      setFormData(prev => ({
        ...prev,
        [field]: {
          file: file,
          preview: e.target.result,
          name: file.name
        }
      }));
      toast.success(`${getFieldLabel(field)} uploaded successfully!`);
    };
    reader.readAsDataURL(file);
  };

  const getFieldLabel = (field) => {
    switch (field) {
      case 'idFront': return 'ID Front';
      case 'idBack': return 'ID Back';
      case 'selfie': return 'Selfie';
      default: return 'Document';
    }
  };

  const removeFile = (field) => {
    setFormData(prev => ({ ...prev, [field]: null }));
    // Reset the file input
    if (field === 'idFront') idFrontRef.current.value = '';
    if (field === 'idBack') idBackRef.current.value = '';
    if (field === 'selfie') selfieRef.current.value = '';
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    
    // Simulate API call with document upload
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // In a real app, you would upload documents to server here
    // const formDataToSend = new FormData();
    // formDataToSend.append('idFront', formData.idFront.file);
    // formDataToSend.append('idBack', formData.idBack.file);
    // formDataToSend.append('selfie', formData.selfie.file);
    // await kycAPI.submitDocuments(formDataToSend);
    
    // Save KYC status
    localStorage.setItem('kyc_status', 'pending');
    setKycStatus('pending');
    setSubmitting(false);
    toast.success('KYC verification submitted! We will review your documents within 24-48 hours.');
  };

  const getStatusBadge = () => {
    switch (kycStatus) {
      case 'approved':
        return (
          <div className="flex items-center gap-2 text-[#00FF94] bg-[#00FF94]/10 px-4 py-2 rounded-full">
            <CheckCircle className="w-5 h-5" />
            <span className="font-semibold">Verified</span>
          </div>
        );
      case 'pending':
        return (
          <div className="flex items-center gap-2 text-[#FFD700] bg-[#FFD700]/10 px-4 py-2 rounded-full">
            <Clock className="w-5 h-5" />
            <span className="font-semibold">Under Review</span>
          </div>
        );
      case 'rejected':
        return (
          <div className="flex items-center gap-2 text-[#FF4444] bg-[#FF4444]/10 px-4 py-2 rounded-full">
            <AlertCircle className="w-5 h-5" />
            <span className="font-semibold">Rejected</span>
          </div>
        );
      default:
        return (
          <div className="flex items-center gap-2 text-gray-400 bg-white/5 px-4 py-2 rounded-full">
            <Shield className="w-5 h-5" />
            <span className="font-semibold">Not Verified</span>
          </div>
        );
    }
  };

  // Document Upload Component
  const DocumentUpload = ({ field, label, icon: Icon, inputRef }) => {
    const doc = formData[field];
    
    return (
      <div className="relative">
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          onChange={(e) => handleFileSelect(field, e)}
          className="hidden"
          data-testid={`file-input-${field}`}
        />
        
        {doc ? (
          // Preview uploaded document
          <div className="border-2 border-[#00FF94] bg-[#00FF94]/5 rounded-xl p-4 relative">
            <button
              onClick={() => removeFile(field)}
              className="absolute top-2 right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
              data-testid={`remove-${field}`}
            >
              <X className="w-4 h-4 text-white" />
            </button>
            
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 rounded-lg overflow-hidden bg-black/20 flex-shrink-0">
                <img 
                  src={doc.preview} 
                  alt={label}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <CheckCircle className="w-5 h-5 text-[#00FF94]" />
                  <p className="text-white font-semibold">{label}</p>
                </div>
                <p className="text-gray-400 text-sm truncate">{doc.name}</p>
                <p className="text-[#00FF94] text-xs mt-1">Uploaded successfully</p>
              </div>
            </div>
          </div>
        ) : (
          // Upload button
          <button
            onClick={() => inputRef.current?.click()}
            className="w-full border-2 border-dashed border-white/20 hover:border-[#7000FF] rounded-xl p-6 text-center cursor-pointer transition-all hover:bg-white/5"
            data-testid={`upload-${field}`}
          >
            <div className="flex flex-col items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center">
                <Icon className="w-6 h-6 text-gray-400" />
              </div>
              <div>
                <p className="text-white font-semibold">{label}</p>
                <p className="text-gray-400 text-sm mt-1">Click to upload from device</p>
                <p className="text-gray-500 text-xs mt-1">JPG, PNG up to 5MB</p>
              </div>
            </div>
          </button>
        )}
      </div>
    );
  };

  // If KYC is pending or approved, show status
  if (kycStatus === 'pending' || kycStatus === 'approved') {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto space-y-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <h1 className="text-4xl font-primary font-bold text-white mb-4">KYC VERIFICATION</h1>
            {getStatusBadge()}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="glass-card p-8 rounded-2xl text-center"
          >
            {kycStatus === 'pending' && (
              <>
                <Clock className="w-16 h-16 text-[#FFD700] mx-auto mb-4" />
                <h2 className="text-2xl font-primary font-bold text-white mb-2">Verification In Progress</h2>
                <p className="text-gray-400 font-secondary">
                  Your documents are being reviewed. This usually takes 24-48 hours.
                  We'll notify you once the verification is complete.
                </p>
              </>
            )}
            {kycStatus === 'approved' && (
              <>
                <CheckCircle className="w-16 h-16 text-[#00FF94] mx-auto mb-4" />
                <h2 className="text-2xl font-primary font-bold text-white mb-2">Verification Complete</h2>
                <p className="text-gray-400 font-secondary">
                  Your identity has been verified. You now have full access to all CryptoPay features.
                </p>
              </>
            )}
          </motion.div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-2xl mx-auto space-y-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-4xl font-primary font-bold text-white mb-2">KYC VERIFICATION</h1>
          <p className="text-gray-400 font-secondary">Complete verification to unlock all features</p>
        </motion.div>

        {/* Progress Steps */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="flex items-center justify-between"
        >
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center flex-1">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                step >= s ? 'bg-[#7000FF] text-white' : 'bg-white/10 text-gray-400'
              }`}>
                {s}
              </div>
              {s < 3 && (
                <div className={`flex-1 h-1 mx-2 rounded ${
                  step > s ? 'bg-[#7000FF]' : 'bg-white/10'
                }`} />
              )}
            </div>
          ))}
        </motion.div>

        {/* Step Labels */}
        <div className="flex justify-between text-sm text-gray-400">
          <span className={step === 1 ? 'text-white' : ''}>Personal Info</span>
          <span className={step === 2 ? 'text-white' : ''}>Address</span>
          <span className={step === 3 ? 'text-white' : ''}>Documents</span>
        </div>

        {/* Form */}
        <motion.div
          key={step}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3 }}
          className="glass-card p-8 rounded-2xl"
        >
          {/* Step 1: Personal Info */}
          {step === 1 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <User className="w-6 h-6 text-[#7000FF]" />
                <h2 className="text-xl font-primary font-bold text-white">Personal Information</h2>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300 mb-2 block">First Name</Label>
                  <Input
                    placeholder="John"
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    className="bg-white/5 border-white/10 text-white"
                    data-testid="kyc-firstname"
                  />
                </div>
                <div>
                  <Label className="text-gray-300 mb-2 block">Last Name</Label>
                  <Input
                    placeholder="Doe"
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    className="bg-white/5 border-white/10 text-white"
                    data-testid="kyc-lastname"
                  />
                </div>
              </div>

              <div>
                <Label className="text-gray-300 mb-2 block">Date of Birth</Label>
                <Input
                  type="date"
                  value={formData.dateOfBirth}
                  onChange={(e) => setFormData({ ...formData, dateOfBirth: e.target.value })}
                  className="bg-white/5 border-white/10 text-white"
                  data-testid="kyc-dob"
                />
              </div>

              <div>
                <Label className="text-gray-300 mb-2 block">Nationality</Label>
                <Select value={formData.nationality} onValueChange={(v) => setFormData({ ...formData, nationality: v })}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="kyc-nationality">
                    <SelectValue placeholder="Select nationality" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A051E] border-white/10">
                    <SelectItem value="uae" className="text-white">United Arab Emirates</SelectItem>
                    <SelectItem value="india" className="text-white">India</SelectItem>
                    <SelectItem value="pakistan" className="text-white">Pakistan</SelectItem>
                    <SelectItem value="philippines" className="text-white">Philippines</SelectItem>
                    <SelectItem value="uk" className="text-white">United Kingdom</SelectItem>
                    <SelectItem value="us" className="text-white">United States</SelectItem>
                    <SelectItem value="other" className="text-white">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button
                onClick={() => setStep(2)}
                disabled={!formData.firstName || !formData.lastName || !formData.dateOfBirth || !formData.nationality}
                className="w-full bg-[#7000FF] hover:bg-[#5B00CC] text-white rounded-full py-6"
                data-testid="kyc-step1-next"
              >
                Continue
              </Button>
            </div>
          )}

          {/* Step 2: Address */}
          {step === 2 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <FileText className="w-6 h-6 text-[#7000FF]" />
                <h2 className="text-xl font-primary font-bold text-white">Address Information</h2>
              </div>

              <div>
                <Label className="text-gray-300 mb-2 block">Street Address</Label>
                <Input
                  placeholder="123 Main Street, Apt 4B"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="bg-white/5 border-white/10 text-white"
                  data-testid="kyc-address"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300 mb-2 block">City</Label>
                  <Input
                    placeholder="Dubai"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="bg-white/5 border-white/10 text-white"
                    data-testid="kyc-city"
                  />
                </div>
                <div>
                  <Label className="text-gray-300 mb-2 block">Postal Code</Label>
                  <Input
                    placeholder="00000"
                    value={formData.postalCode}
                    onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                    className="bg-white/5 border-white/10 text-white"
                    data-testid="kyc-postal"
                  />
                </div>
              </div>

              <div>
                <Label className="text-gray-300 mb-2 block">Country</Label>
                <Select value={formData.country} onValueChange={(v) => setFormData({ ...formData, country: v })}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="kyc-country">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A051E] border-white/10">
                    <SelectItem value="UAE" className="text-white">United Arab Emirates</SelectItem>
                    <SelectItem value="SA" className="text-white">Saudi Arabia</SelectItem>
                    <SelectItem value="QA" className="text-white">Qatar</SelectItem>
                    <SelectItem value="KW" className="text-white">Kuwait</SelectItem>
                    <SelectItem value="BH" className="text-white">Bahrain</SelectItem>
                    <SelectItem value="OM" className="text-white">Oman</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={() => setStep(1)}
                  className="flex-1 bg-white/10 hover:bg-white/20 text-white rounded-full py-6"
                >
                  Back
                </Button>
                <Button
                  onClick={() => setStep(3)}
                  disabled={!formData.address || !formData.city}
                  className="flex-1 bg-[#7000FF] hover:bg-[#5B00CC] text-white rounded-full py-6"
                  data-testid="kyc-step2-next"
                >
                  Continue
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Documents */}
          {step === 3 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <Camera className="w-6 h-6 text-[#7000FF]" />
                <h2 className="text-xl font-primary font-bold text-white">Identity Documents</h2>
              </div>

              <div>
                <Label className="text-gray-300 mb-2 block">ID Type</Label>
                <Select value={formData.idType} onValueChange={(v) => setFormData({ ...formData, idType: v })}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="kyc-id-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A051E] border-white/10">
                    <SelectItem value="passport" className="text-white">Passport</SelectItem>
                    <SelectItem value="emirates_id" className="text-white">Emirates ID</SelectItem>
                    <SelectItem value="national_id" className="text-white">National ID</SelectItem>
                    <SelectItem value="driving_license" className="text-white">Driving License</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-gray-300 mb-2 block">ID Number</Label>
                <Input
                  placeholder="Enter your ID number"
                  value={formData.idNumber}
                  onChange={(e) => setFormData({ ...formData, idNumber: e.target.value })}
                  className="bg-white/5 border-white/10 text-white"
                  data-testid="kyc-id-number"
                />
              </div>

              {/* Document Uploads */}
              <div className="space-y-4">
                <Label className="text-gray-300 block">Upload Documents</Label>
                <p className="text-gray-500 text-sm -mt-2 mb-4">
                  Take a photo or select from your device gallery. Ensure documents are clear and readable.
                </p>
                
                <DocumentUpload 
                  field="idFront" 
                  label="ID Front Side" 
                  icon={Image}
                  inputRef={idFrontRef}
                />

                <DocumentUpload 
                  field="idBack" 
                  label="ID Back Side" 
                  icon={Image}
                  inputRef={idBackRef}
                />

                <DocumentUpload 
                  field="selfie" 
                  label="Selfie with ID" 
                  icon={Camera}
                  inputRef={selfieRef}
                />
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={() => setStep(2)}
                  className="flex-1 bg-white/10 hover:bg-white/20 text-white rounded-full py-6"
                >
                  Back
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={submitting || !formData.idNumber || !formData.idFront || !formData.idBack || !formData.selfie}
                  className="flex-1 bg-[#00FF94] hover:bg-[#00CC77] text-black rounded-full py-6 font-bold"
                  data-testid="kyc-submit"
                >
                  {submitting ? 'Submitting...' : 'Submit for Verification'}
                </Button>
              </div>
            </div>
          )}
        </motion.div>

        {/* Info Box */}
        <div className="bg-[#7000FF]/10 border border-[#7000FF]/30 rounded-xl p-4 text-sm">
          <p className="text-[#7000FF] font-semibold mb-1">Why do we need this?</p>
          <p className="text-gray-400">
            KYC verification is required by UAE regulations to prevent fraud and money laundering. 
            Your data is encrypted and securely stored.
          </p>
        </div>
      </div>
    </Layout>
  );
};

export default KYC;
